import React, { useState } from 'react';
import SEOHead from '../components/SEOHead';
import { 
  Heart, Users, Trophy, Leaf, Recycle, Lightbulb, 
  FileCheck, AlertCircle, FileText, Download, 
  CheckCircle2, Globe, Sprout, HandHeart, Shield,
  Monitor, Smartphone, Building2, Target, TrendingUp,
  Award, Eye, MessageSquare, ArrowRight
} from 'lucide-react';
import AnimatedSection from '../components/AnimatedSection';
import PageTransition from '../components/PageTransition';
import GradientHero from '../components/GradientHero';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const CSR = () => {
  const [showInclusion, setShowInclusion] = useState(false);
  const [showChecklist, setShowChecklist] = useState(false);

  const csrJsonLd = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "RSE & Engagements - Neoqualis",
    "description": "Découvrez nos engagements RSE : parité, accessibilité PMR, numérique responsable, achats durables",
    "mainEntity": {
      "@type": "Organization",
      "name": "Neoqualis",
      "description": "ESN française engagée en RSE"
    }
  };

  const philosophy = [
    {
      title: 'Authentique',
      description: 'Priorité aux actions terrain avant la communication',
      icon: Heart,
      color: 'primary'
    },
    {
      title: 'Progressive',
      description: 'Diagnostiquer → agir → mesurer → améliorer',
      icon: TrendingUp,
      color: 'secondary'
    },
    {
      title: 'Adaptée ESN',
      description: 'Sécurité, RGPD, qualité des délivrables consultants',
      icon: Shield,
      color: 'green-600'
    },
    {
      title: 'Ancrée localement',
      description: 'Partenariats écoles/associations, inclusion des jeunes',
      icon: Building2,
      color: 'blue-600'
    }
  ];

  const inclusionActions = [
    {
      title: 'Parité équilibrée',
      description: 'Équipe équilibrée (50% / 50%)',
      icon: Users
    },
    {
      title: 'Jeunes talents',
      description: 'Alternants & stagiaires intégrés chaque année (tutorat, plan de progression)',
      icon: Trophy
    },
    {
      title: 'Accessibilité PMR',
      description: 'Bureaux pleinement accessibles (accès, sanitaires adaptés, largeurs conformes)',
      icon: HandHeart
    },
    {
      title: 'Tolérance zéro',
      description: 'Process RH transparent (recrutement & progression)',
      icon: Shield
    }
  ];

  const digitalResponsible = [
    {
      title: 'Sobriété d\'usage',
      description: 'Éco-gestes, tri sélectif, collecte piles/cartouches',
      icon: Leaf
    },
    {
      title: 'Hébergement sobre',
      description: 'Priorité aux hébergeurs sobres, optimisation pipelines/tests',
      icon: Monitor
    },
    {
      title: 'Mesure & suivi',
      description: 'Suivi consommations (eau, papier)',
      icon: Target
    }
  ];

  const digitalObjectives = [
    '-20% d\'impressions papier',
    '-15% d\'exécutions CI inutiles',
    '100% formations onboarding avec module éco-gestes'
  ];

  const responsiblePurchasing = [
    {
      title: 'Réemploi d\'abord',
      description: 'Rechercher seconde main / reconditionné avant tout achat neuf',
      icon: Recycle
    },
    {
      title: 'Réparabilité',
      description: 'Privilégier modèles réparables, pièces disponibles, contrats maintenance',
      icon: Trophy
    },
    {
      title: 'Matériaux & labels',
      description: 'Si achat neuf : bois FSC/PEFC et matériaux recyclés',
      icon: Leaf
    },
    {
      title: 'Fin de vie',
      description: 'Réemploi interne, don associatif, filières DEEE/recyclage',
      icon: Globe
    }
  ];

  const purchasingObjectives = [
    '≥ 60% des achats IT en reconditionné',
    '≥ 80% du mobilier issu du réemploi',
    '100% équipements fin de vie traités via réemploi/don/recyclage'
  ];

  const qvtActions = [
    {
      title: 'QVT',
      description: 'Mutuelle, titres-restaurant, primes, télétravail, coaching mission',
      icon: Heart
    },
    {
      title: 'Santé & sécurité',
      description: 'DUERP à jour, sensibilisation ergonomie, canaux signalement',
      icon: Shield
    },
    {
      title: 'Management',
      description: 'Proximité direction, feedbacks réguliers, rituels équipe',
      icon: Users
    }
  ];

  const indicators = [
    { label: 'Parité H/F : 50% / 50%', status: 'atteint', color: 'green' },
    { label: 'Locaux PMR accessibles', status: 'atteint', color: 'green' },
    { label: 'Achats IT reconditionnés : 60%', status: 'en-cours', color: 'orange' },
    { label: 'Mobilier réemployé : 80%', status: 'en-cours', color: 'orange' },
    { label: 'Impressions papier : -20%', status: 'cible', color: 'blue' },
    { label: 'Exécutions CI inutiles : -15%', status: 'cible', color: 'blue' },
    { label: 'Traitement fin de vie : 100%', status: 'cible', color: 'blue' }
  ];

  const roadmapTrimester = [
    'Publication Charte éthique & RSE + Politique Achats responsables',
    'Module onboarding sobriété numérique pour tous les nouveaux',
    'Cartographie d\'accessibilité des locaux (parcours PMR, signalétique)'
  ];

  const roadmap12Months = [
    'Comité RSE rattaché à la direction (suivi trimestriel)',
    'Programme éducation numérique (interventions écoles/associations)',
    'Étude de label (ex. ISO 14001 ou équivalent) si pertinence confirmée'
  ];

  const documents = [
    { title: 'Charte éthique & RSE', status: 'Publication à venir', icon: FileText },
    { title: 'Politique Achats responsables', status: 'Publication à venir', icon: FileText },
    { title: 'DUERP', status: 'Consultable sur demande', icon: Shield },
    { title: 'Guide éco-gestes', status: 'Disponible en PDF', icon: Download }
  ];

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.5
      }
    })
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'atteint': return 'text-green-600 bg-green-100';
      case 'en-cours': return 'text-orange-600 bg-orange-100';
      case 'cible': return 'text-blue-600 bg-blue-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'atteint': return 'Atteint';
      case 'en-cours': return 'En cours';
      case 'cible': return 'Cible';
      default: return status;
    }
  };

  return (
    <PageTransition>
      <SEOHead 
        title="RSE & Engagements — Parité, Accessibilité, Numérique Responsable | Neoqualis"
        description="Découvrez nos engagements RSE concrets : parité 50/50, locaux PMR accessibles, numérique responsable, achats durables. Une ESN française engagée pour un impact positif."
        canonical="https://neoqualis.com/csr"
        jsonLd={csrJsonLd}
      />
      <div className="pt-16">
        {/* Hero Section */}
        <GradientHero
          title="Engagement humain & RSE"
          subtitle="Une RSE en action : des choix concrets (achats responsables, accessibilité PMR, 
                sobriété numérique), des indicateurs suivis et un impact local."
        />

        {/* Notre philosophie */}
        <section className="section bg-white">
          <div className="container">
            <AnimatedSection className="text-center mb-12">
              <h2 className="heading">Notre philosophie</h2>
              <p className="subheading max-w-2xl mx-auto">
                Une approche RSE authentique et progressive
              </p>
            </AnimatedSection>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
              {philosophy.map(({ title, description, icon: Icon, color }, i) => (
                <motion.div
                  key={title}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={cardVariants}
                >
                  <div className="card h-full text-center hover:scale-105 transition-transform">
                    <div className="flex justify-center mb-4">
                      <div className={`p-3 rounded-full bg-${color} bg-opacity-10`}>
                        <Icon className={`w-8 h-8 text-${color}`} />
                      </div>
                    </div>
                    <h3 className="text-xl font-semibold mb-3 text-gray-900">{title}</h3>
                    <p className="text-gray-700">{description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Parité, diversité & inclusion */}
        <section className="section bg-gray-50">
          <div className="container">
            <AnimatedSection className="text-center mb-12">
              <h2 className="heading">Parité, diversité & inclusion</h2>
              <p className="subheading max-w-2xl mx-auto">Un engagement concret pour l'égalité et l'inclusion</p>
            </AnimatedSection>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto mb-8">
              {inclusionActions.map(({ title, description, icon: Icon }, i) => (
                <motion.div key={title} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={cardVariants}>
                  <div className="card h-full hover:scale-105 transition-transform">
                    <div className="flex items-center mb-4">
                      <div className="p-3 rounded-full bg-primary bg-opacity-10">
                        <Icon className="w-8 h-8 text-primary" />
                      </div>
                      <h3 className="text-xl font-semibold ml-4 text-gray-900">{title}</h3>
                    </div>
                    <p className="text-gray-700">{description}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* CTA -> bloc expansible */}
            {/* <div className="text-center mt-6">
              <button
                onClick={() => setShowInclusion(!showInclusion)}
                aria-expanded={showInclusion}
                aria-controls="inclusion-details"
                className="text-primary font-semibold inline-flex items-center hover:underline"
              >
                {showInclusion ? "Fermer les actions inclusion & PMR" : "Voir nos actions inclusion & PMR"}
                <ArrowRight className="ml-2 w-4 h-4" />
              </button>

              {showInclusion && (
                <div id="inclusion-details" className="card bg-white shadow-md max-w-3xl mx-auto mt-6 p-6 text-left">
                  <h3 className="text-xl font-bold text-primary mb-4">Nos actions concrètes</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-700">
                    <li><strong>Parité</strong> : équilibre H/F (48% / 52%) dans nos équipes.</li>
                    <li><strong>Accessibilité PMR</strong> : bureaux et sanitaires adaptés, largeurs conformes.</li>
                    <li><strong>Inclusion jeunes</strong> : accueil régulier d’alternants et stagiaires (tutorat, plan de progression).</li>
                    <li><strong>Tolérance zéro</strong> : processus RH transparents pour le recrutement et l’évolution.</li>
                  </ul>
                  <p className="mt-4 text-sm text-gray-600 italic">
                    ➜ Chantier en cours : formalisation d’une charte inclusion & diversité et publication d’indicateurs annuels.
                  </p>
                </div>
              )}
            </div> */}
          </div>
        </section>

        {/* Numérique responsable */}
<section className="section bg-white">
  <div className="container">
    <AnimatedSection className="text-center mb-12">
      <h2 className="heading">Numérique responsable</h2>
      <p className="subheading max-w-2xl mx-auto">
        Sobriété numérique et mesure de l'impact
      </p>
    </AnimatedSection>

    {/* Cartes "digitalResponsible" */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-12">
      {digitalResponsible.map(({ title, description, icon: Icon }, i) => (
        <motion.div
          key={title}
          custom={i}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={cardVariants}
        >
          <div className="card h-full hover:scale-105 transition-transform">
            <div className="flex items-center mb-4">
              <div className="p-3 rounded-full bg-green-100">
                <Icon className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold ml-4 text-gray-900">{title}</h3>
            </div>
            <p className="text-gray-700">{description}</p>
          </div>
        </motion.div>
      ))}
    </div>

    {/* Bloc Checklist Éco-gestes */}
    <div className="text-center mb-6">
      <button
        onClick={() => setShowChecklist(!showChecklist)}
        className="btn-primary inline-flex items-center"
      >
        {showChecklist ? "Masquer la checklist" : "Voir la checklist éco-gestes"}
        <ArrowRight className="ml-2" />
      </button>
    </div>

    {showChecklist && (
      <AnimatedSection>
        <div className="card bg-green-50 shadow-lg max-w-4xl mx-auto p-6 text-left">
          <h3 className="text-2xl font-bold text-green-700 mb-4">
            ✅ Checklist éco-gestes - Neoqualis
          </h3>

          <div className="space-y-6 text-gray-700">
            <div>
              <h4 className="font-semibold mb-2">🌍 Sobriété numérique</h4>
              <ul className="list-disc list-inside space-y-1">
                <li>Éteindre son poste chaque soir (PC, écrans, chargeurs).</li>
                <li>Couper la caméra si non nécessaire lors des visios.</li>
                <li>Nettoyer mails & espaces de stockage (limiter les doublons).</li>
                <li>Limiter les impressions (recto/verso, noir & blanc).</li>
                <li>Supprimer les exécutions CI/CD inutiles.</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-2">🖨️ Impressions & papier</h4>
              <ul className="list-disc list-inside space-y-1">
                <li>Demander si l’impression est indispensable.</li>
                <li>Utiliser du papier recyclé (FSC/PEFC).</li>
                <li>Imprimer en recto-verso, brouillon par défaut.</li>
                <li>Favoriser les signatures électroniques.</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-2">🔋 Énergie & matériel</h4>
              <ul className="list-disc list-inside space-y-1">
                <li>Débrancher les chargeurs inutilisés.</li>
                <li>Mise en veille auto : écran 5 min, PC 10 min.</li>
                <li>Favoriser le matériel reconditionné.</li>
                <li>Réparer plutôt que remplacer.</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-2">🚮 Tri & recyclage</h4>
              <ul className="list-disc list-inside space-y-1">
                <li>Trier papiers, plastiques, cartouches, piles.</li>
                <li>Donner les équipements en fin de vie (écoles, associations).</li>
                <li>Privilégier le réemploi avant l’achat neuf.</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-2">🚆 Déplacements & mobilité</h4>
              <ul className="list-disc list-inside space-y-1">
                <li>Favoriser train ou covoiturage.</li>
                <li>Utiliser la visio si déplacement non nécessaire.</li>
                <li>Marcher ou prendre le vélo pour les trajets courts.</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-2">💡 Bonnes pratiques collectives</h4>
              <ul className="list-disc list-inside space-y-1">
                <li>Sensibiliser les nouveaux via l’onboarding.</li>
                <li>Créer un canal Teams/Slack “#eco-gestes”.</li>
                <li>Suivre des indicateurs simples (papier, CI/CD, énergie).</li>
                <li>Valoriser les réussites collectives (ex : 20% impressions en moins).</li>
              </ul>
            </div>
          </div>
        </div>
      </AnimatedSection>
    )}
  </div>
</section>

        {/* Achats responsables */}
        <section className="section bg-gray-50">
          <div className="container">
            <AnimatedSection className="text-center mb-12">
              <h2 className="heading">Achats responsables (IT & mobilier)</h2>
              <p className="subheading max-w-2xl mx-auto">
                Réemploi, réparabilité et fin de vie maîtrisée
              </p>
            </AnimatedSection>

            {/* Synthèse en 5 points */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 max-w-6xl mx-auto mb-12">
              {[
                { title: "Refuser", desc: "Questionner la nécessité de chaque achat", icon: AlertCircle },
                { title: "Réduire", desc: "Mutualiser, optimiser l’usage, privilégier la location", icon: Lightbulb },
                { title: "Réutiliser", desc: "Favoriser le reconditionné, la seconde main et l’upcycling", icon: Recycle },
                { title: "Recycler", desc: "Privilégier les filières certifiées en fin de vie", icon: Leaf },
                { title: "Critères de sélection", desc: "Labels (FSC, EPEAT…), conditions sociales, TCO", icon: CheckCircle2 }
              ].map(({ title, desc, icon: Icon }, i) => (
                <motion.div
                  key={title}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={cardVariants}
                >
                  <div className="card h-full text-center hover:scale-105 transition-transform">
                    <div className="flex justify-center mb-4">
                      <div className="p-3 rounded-full bg-secondary bg-opacity-10">
                        <Icon className="w-8 h-8 text-secondary" />
                      </div>
                    </div>
                    <h3 className="text-lg font-semibold mb-3 text-gray-900">{title}</h3>
                    <p className="text-gray-700 text-sm">{desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Focus IT & Mobilier */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-8">
              <AnimatedSection>
                <div className="card">
                  <div className="flex items-center mb-4">
                    <Monitor className="w-8 h-8 text-primary mr-3" />
                    <h3 className="text-xl font-semibold text-gray-900">Équipements IT</h3>
                  </div>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Ordinateurs & smartphones : reconditionné (grade A/B) par défaut</li>
                    <li>• Accessoires : matériaux recyclés, réparables</li>
                    <li>• Périphériques : seconde main ou réparés</li>
                    <li>• Mutualisation écrans et stations d’accueil</li>
                  </ul>
                </div>
              </AnimatedSection>

              <AnimatedSection delay={0.1}>
                <div className="card">
                  <div className="flex items-center mb-4">
                    <Building2 className="w-8 h-8 text-primary mr-3" />
                    <h3 className="text-xl font-semibold text-gray-900">Mobilier de bureau</h3>
                  </div>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Bureaux, chaises, rangements : seconde main priorité</li>
                    <li>• Recours à l’upcycling quand pertinent</li>
                    <li>• Achat neuf seulement si nécessaire (ergonomie/PMR)</li>
                    <li>• Critères recyclé / labellisé si achat neuf</li>
                  </ul>
                </div>
              </AnimatedSection>
            </div>

            {/* Objectifs + CTA */}
            <AnimatedSection>
              <div className="card bg-secondary bg-opacity-5 max-w-4xl mx-auto">
                <h3 className="text-xl font-semibold mb-6 text-gray-900">Objectifs 12–18 mois</h3>
                <div className="space-y-3">
                  {purchasingObjectives.map((objective, i) => (
                    <div key={i} className="flex items-center">
                      <Target className="w-5 h-5 text-secondary mr-3" />
                      <span className="text-gray-700">{objective}</span>
                    </div>
                  ))}
                </div>
                {/* <div className="mt-6 text-center">
                  <Link 
                    to="/documents/politique-achats-responsables.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-secondary font-semibold inline-flex items-center hover:underline"
                  >
                    Consulter notre politique Achats responsables
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </div> */}
              </div>
            </AnimatedSection>
          </div>
        </section>

        {/* Qualité de Vie au Travail & sécurité */}
        <section className="section bg-white">
          <div className="container">
            <AnimatedSection className="text-center mb-12">
              <h2 className="heading">Qualité de Vie au Travail & sécurité</h2>
              <p className="subheading max-w-2xl mx-auto">
                Bien-être, santé et management de proximité
              </p>
            </AnimatedSection>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-8">
              {qvtActions.map(({ title, description, icon: Icon }, i) => (
                <motion.div
                  key={title}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={cardVariants}
                >
                  <div className="card h-full hover:scale-105 transition-transform">
                    <div className="flex items-center mb-4">
                      <div className="p-3 rounded-full bg-blue-100">
                        <Icon className="w-8 h-8 text-blue-600" />
                      </div>
                      <h3 className="text-xl font-semibold ml-4 text-gray-900">{title}</h3>
                    </div>
                    <p className="text-gray-700">{description}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* <div className="text-center">
              <Link 
                to="/contact?subject=qvt"
                className="text-blue-600 font-semibold inline-flex items-center hover:underline"
              >
                Découvrir nos initiatives QVT
                <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </div> */}
          </div>
        </section>

        {/* Indicateurs & suivi */}
        <section className="section bg-gray-50">
          <div className="container">
            <AnimatedSection className="text-center mb-12">
              <h2 className="heading">Indicateurs & suivi</h2>
              <p className="subheading max-w-2xl mx-auto">
                Snapshot de nos engagements et objectifs
              </p>
            </AnimatedSection>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {indicators.map(({ label, status }, i) => (
                <motion.div
                  key={label}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={cardVariants}
                >
                  <div className="card h-full">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700 text-sm">{label}</span>
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${getStatusColor(status)}`}>
                        {getStatusLabel(status)}
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Gouvernance & feuille de route */}
        <section className="section bg-white">
          <div className="container">
            <AnimatedSection className="text-center mb-12">
              <h2 className="heading">Gouvernance & feuille de route</h2>
              <p className="subheading max-w-2xl mx-auto">
                Nos prochaines étapes et engagements
              </p>
            </AnimatedSection>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              <AnimatedSection>
                <div className="card h-full">
                  <div className="flex items-center mb-6">
                    <div className="p-3 rounded-full bg-primary bg-opacity-10">
                      <Target className="w-8 h-8 text-primary" />
                    </div>
                    <h3 className="text-xl font-semibold ml-4 text-gray-900">Trimestre à venir</h3>
                  </div>
                  <ul className="space-y-3">
                    {roadmapTrimester.map((item, i) => (
                      <li key={i} className="flex items-start">
                        <CheckCircle2 className="w-5 h-5 text-primary mr-3 mt-0.5" />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </AnimatedSection>

              <AnimatedSection delay={0.1}>
                <div className="card h-full">
                  <div className="flex items-center mb-6">
                    <div className="p-3 rounded-full bg-secondary bg-opacity-10">
                      <TrendingUp className="w-8 h-8 text-secondary" />
                    </div>
                    <h3 className="text-xl font-semibold ml-4 text-gray-900">12–18 mois</h3>
                  </div>
                  <ul className="space-y-3">
                    {roadmap12Months.map((item, i) => (
                      <li key={i} className="flex items-start">
                        <Target className="w-5 h-5 text-secondary mr-3 mt-0.5" />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </AnimatedSection>
            </div>
          </div>
        </section>

        {/* Ressources & documents
        <section className="section bg-gray-50">
          <div className="container">
            <AnimatedSection className="text-center mb-12">
              <h2 className="heading">Ressources & documents</h2>
              <p className="subheading max-w-2xl mx-auto">
                Accédez à nos politiques et guides
              </p>
            </AnimatedSection>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
              {documents.map(({ title, status, icon: Icon }, i) => (
                <motion.div
                  key={title}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={cardVariants}
                >
                  <div className="card h-full text-center hover:scale-105 transition-transform">
                    <div className="flex justify-center mb-4">
                      <div className="p-3 rounded-full bg-primary bg-opacity-10">
                        <Icon className="w-8 h-8 text-primary" />
                      </div>
                    </div>
                    <h3 className="text-lg font-semibold mb-2 text-gray-900">{title}</h3>
                    <p className="text-sm text-gray-600">{status}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section> */}

        {/* CTA final */}
        <section className="section bg-white">
          <div className="container">
            <AnimatedSection className="text-center">
              <div className="max-w-3xl mx-auto">
                <h2 className="heading mb-6">
                  Vous souhaitez prioriser des actions à impact ?
                </h2>
                <p className="subheading mb-8">
                  Achats responsables, accessibilité, sobriété numérique : 
                  mesurez vos progrès avec notre accompagnement.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link 
                    to="/contact?subject=rse"
                    className="btn-primary inline-flex items-center"
                  >
                    Échanger avec notre référent RSE
                    <MessageSquare className="ml-2" />
                  </Link>
                  <Link 
                    to="/contact?subject=achats-responsables"
                    className="btn-secondary inline-flex items-center"
                  >
                    Recevoir notre politique Achats responsables
                    <Download className="ml-2" />
                  </Link>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </section>
      </div>
    </PageTransition>
  );
};

export default CSR;